import { getContrastRatio } from '@mui/material/styles';

/**
 * Background colours for avatars with white initials. Every colour reaches
 * 4.5:1 against #fff (WCAG AA for text); avatarUtils.test.ts checks this.
 */
export const AVATAR_PALETTE = [
  '#0072bc', // brand blue
  '#006b8f', // petrol
  '#00796b', // teal
  '#2e7d32', // green
  '#3949ab', // indigo
  '#5e35b1', // deep purple
  '#8e24aa', // purple
  '#c2185b', // pink
  '#bf360c', // burnt orange
  '#455a64', // blue grey
] as const;

export const AVATAR_TEXT_COLOR = '#fff';

/**
 * Avatar colour for a name or username: the same input always gets the same
 * colour, so a person keeps their colour on every screen and in every list.
 */
export const stringToColor = (string: string) => {
  let hash = 0;

  /* eslint-disable no-bitwise */
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }

  return AVATAR_PALETTE[(hash >>> 0) % AVATAR_PALETTE.length];
  /* eslint-enable no-bitwise */
};

export const hexToRgb = (hex: string): number[] | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result && result.length === 4 ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
};

export const takeFirstWordOfString = (word: string) => {
  const nameLength = word.split(' ');
  return nameLength.length > 1 ? `${word.split(' ')[0][0]}${word.split(' ')[1][0]}` : `${word.split(' ')[0][0]}`;
};

/**
 * Initials colour on any avatar background (e.g. a workspace's own colour):
 * white when it reaches 4.5:1, otherwise black, which then always does.
 */
export const avatarTextColorOn = (background: string) => (getContrastRatio(AVATAR_TEXT_COLOR, background) >= 4.5 ? AVATAR_TEXT_COLOR : '#000');

/** sx colours for an avatar keyed by a name or username. */
export const avatarColorSx = (key: string) => ({
  bgcolor: stringToColor(key),
  color: AVATAR_TEXT_COLOR,
});

export const stringAvatar = (name: string) => ({
  sx: avatarColorSx(name),
  children: takeFirstWordOfString(name),
});
